import { useEffect, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Loader2, AlertTriangle, Clock } from 'lucide-react'
import { useContextEngineStore } from '@/stores/contextEngineStore'
import { HistoryTimeline } from '@/components/context/HistoryTimeline'
import { ALL_DOMAINS, DOMAIN_LABELS } from '@/types/contextEngine'
import type { ContextDomain } from '@/types/contextEngine'

export function HistoryPage() {
  const {
    history,
    historyLoading,
    historyError,
    historyHasMore,
    fetchHistory,
    revertChange,
  } = useContextEngineStore()

  const [domainFilter, setDomainFilter] = useState<ContextDomain | ''>('')

  useEffect(() => {
    fetchHistory(domainFilter || undefined, true)
  }, [fetchHistory, domainFilter])

  const handleLoadMore = useCallback(() => {
    fetchHistory(domainFilter || undefined)
  }, [fetchHistory, domainFilter])

  // Loading (initial)
  if (historyLoading && history.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-accent-amber" size={32} />
      </div>
    )
  }

  // Error
  if (historyError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center p-4">
        <AlertTriangle size={24} className="text-accent-error" />
        <p className="text-text-secondary">Couldn&apos;t load history.</p>
        <button
          onClick={() => fetchHistory(domainFilter || undefined, true)}
          className="text-sm text-accent-amber hover:underline"
        >
          Retry
        </button>
      </div>
    )
  }

  // Empty
  if (history.length === 0 && !historyLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-[900px] mx-auto">
        <PageHeader domainFilter={domainFilter} onDomainChange={setDomainFilter} />
        <EmptyState />
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-[900px] mx-auto space-y-6">
      <PageHeader domainFilter={domainFilter} onDomainChange={setDomainFilter} />

      <HistoryTimeline changes={history} onRevert={revertChange} />

      {/* Load more */}
      {historyHasMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={handleLoadMore}
            disabled={historyLoading}
            className="inline-flex items-center gap-2 rounded-md bg-bg-elevated px-4 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-surface-hover hover:text-text-primary disabled:opacity-50"
          >
            {historyLoading ? (
              <Loader2 className="animate-spin" size={14} />
            ) : null}
            Load older changes
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Page header with domain filter ──────────────────────────────────────────

function PageHeader({
  domainFilter,
  onDomainChange,
}: {
  domainFilter: ContextDomain | ''
  onDomainChange: (domain: ContextDomain | '') => void
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="font-display text-2xl font-bold text-text-primary mb-1">History</h1>
        <p className="text-sm text-text-secondary">
          Timeline of changes to your context.
        </p>
      </div>
      <select
        value={domainFilter}
        onChange={(e) => onDomainChange(e.target.value as ContextDomain | '')}
        className="rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent-amber transition-colors"
      >
        <option value="">All domains</option>
        {ALL_DOMAINS.map((d) => (
          <option key={d} value={d}>{DOMAIN_LABELS[d]}</option>
        ))}
      </select>
    </div>
  )
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      className="flex flex-col items-center justify-center py-16 text-center"
    >
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent-amber/10">
        <Clock size={32} className="text-accent-amber" />
      </div>
      <h2 className="font-display text-lg font-semibold text-text-primary mb-1">
        No changes yet
      </h2>
      <p className="text-sm text-text-secondary max-w-sm">
        Your context history will appear as you build. Upload documents or start an interview to begin.
      </p>
    </motion.div>
  )
}
